import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert } from 'react-bootstrap';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, TooltipProps, XAxis, YAxis } from 'recharts';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { formatDateOnlyForStorage } from '../../Helpers/DateUtils';
import { DateActivity, useArchiveActivity } from '../../hooks/archive/useArchiveChangeLog';
import { formatArchiveDate, parseArchiveDateParam } from './archiveDateIndex';
import ArchiveDayModal from './ArchiveDayModal';
import SuperadminBreadcrumb from '../Superadmin/SuperadminBreadcrumb';
import './ArchiveDashboard.css';

const BAR_COLOR = '#1F7FA6';

const ActivityTooltip: React.FC<TooltipProps<number, string>> = ({ active, payload }) => {
    const activity = payload?.[0]?.payload as DateActivity | undefined;
    if (!active || !activity) return null;
    return (
        <div className="archive-chart-tooltip">
            <div className="archive-chart-tooltip-date">{formatArchiveDate(activity.date, 'long')}</div>
            {activity.componentCount > 0
                ? <div>{activity.componentCount} komponenter, {activity.buildingCount} byggnader</div>
                : <div>Inga ändringar</div>}
        </div>
    );
};

const ArchiveDashboard: React.FC = () => {
    useDocumentTitle('Ändringslogg');

    const [searchParams, setSearchParams] = useSearchParams();
    const selectedDate = parseArchiveDateParam(searchParams.get('date'));

    // Default period: the last 30 days
    const today = formatDateOnlyForStorage(new Date());
    const [startDate, setStartDate] = useState<string>(
        formatDateOnlyForStorage(new Date(Date.now() - 29 * 24 * 60 * 60 * 1000))
    );
    const [endDate, setEndDate] = useState<string>(today);

    const { activities, loading, error } = useArchiveActivity(startDate, endDate);

    const openDate = (date: string) => {
        const next = new URLSearchParams(searchParams);
        next.set('date', date);
        setSearchParams(next);
    };

    const closeDate = () => {
        const next = new URLSearchParams(searchParams);
        next.delete('date');
        setSearchParams(next);
    };

    const totalChanges = activities.reduce((sum, d) => sum + d.componentCount, 0);
    const datesWithActivity = activities.filter(d => d.componentCount > 0).length;
    const maxChangesInDay = Math.max(...activities.map(d => d.componentCount), 0);

    return (
        <>
        <SuperadminBreadcrumb title="Ändringslogg" />
        <div className="archive-dashboard">
            <div className="archive-header">
                <h1 className="page-title">Komponentändringar</h1>
                <p className="archive-subtitle">Antal ändrade komponenter per dag. Klicka på en dag för att se ändringarna.</p>
            </div>

            <div className="archive-controls">
                <div className="date-range-selector">
                    <div className="date-input-group">
                        <label htmlFor="start-date">Från:</label>
                        <input
                            id="start-date"
                            type="date"
                            value={startDate}
                            onChange={(e) => e.target.value && setStartDate(e.target.value)}
                            max={endDate}
                        />
                    </div>
                    <div className="date-input-group">
                        <label htmlFor="end-date">Till:</label>
                        <input
                            id="end-date"
                            type="date"
                            value={endDate}
                            onChange={(e) => e.target.value && setEndDate(e.target.value)}
                            min={startDate}
                            max={today}
                        />
                    </div>
                </div>

                <div className="archive-stats">
                    <div className="stat-card">
                        <span className="stat-value">{totalChanges}</span>
                        <span className="stat-label">Totalt ändringar</span>
                    </div>
                    <div className="stat-card">
                        <span className="stat-value">{datesWithActivity}</span>
                        <span className="stat-label">Aktiva dagar</span>
                    </div>
                    <div className="stat-card">
                        <span className="stat-value">{maxChangesInDay}</span>
                        <span className="stat-label">Max per dag</span>
                    </div>
                </div>
            </div>

            {error && <Alert variant="danger">{error}</Alert>}

            <div className="archive-chart-card">
                {loading ? (
                    <div className="archive-loading">
                        <div className="spinner"></div>
                        <p>Laddar översikt...</p>
                    </div>
                ) : (
                    <ResponsiveContainer width="100%" height={320}>
                        <BarChart
                            data={activities}
                            margin={{ top: 8, right: 8, bottom: 0, left: -16 }}
                            onClick={(state) => {
                                // Clicking anywhere in a day's column opens it, not only the bar
                                const activity = activities[state?.activeTooltipIndex ?? -1];
                                if (activity && activity.componentCount > 0) openDate(activity.date);
                            }}
                            style={{ cursor: 'pointer' }}
                        >
                            <CartesianGrid vertical={false} stroke="#e9ecef" />
                            <XAxis
                                dataKey="date"
                                tickFormatter={(date: string) => formatArchiveDate(date, 'short')}
                                tick={{ fontSize: 12, fill: '#6c757d' }}
                                tickLine={false}
                                axisLine={{ stroke: '#dee2e6' }}
                                minTickGap={16}
                            />
                            <YAxis
                                allowDecimals={false}
                                tick={{ fontSize: 12, fill: '#6c757d' }}
                                tickLine={false}
                                axisLine={false}
                            />
                            <Tooltip content={<ActivityTooltip />} cursor={{ fill: 'rgba(31, 127, 166, 0.08)' }} />
                            <Bar dataKey="componentCount" fill={BAR_COLOR} radius={[4, 4, 0, 0]} maxBarSize={24} />
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </div>

            <ArchiveDayModal date={selectedDate} onHide={closeDate} />
        </div>
        </>
    );
};

export default ArchiveDashboard;
